import { ActionButtons } from '@brightdrop/bd-ui';
import {
  Box,
  ClickAwayListener,
  Unstable_TrapFocus as FocusTrap,
  Paper,
  Popover,
  Popper,
  Typography,
} from '@mui/material';
import { useCallback, useEffect } from 'react';

import type { FilterPopoverProps } from './FilterPopover.model';
import useStyles from './FilterPopover.styles';

const POPOVER_MAX_HEIGHT = '400px';

const STABLE_ANCHOR_POPPER_MODIFIERS = [
  {
    name: 'flip',
    enabled: false,
  },
  {
    name: 'offset',
    options: {
      offset: [0, 4],
    },
  },
];

const FilterPopover = <T,>({
  openPopover,
  onClosePopover,
  component: PopoverComponent,
  disableSubmit,
  hideTitle = false,
  entity,
  entityId,
  title,
  onResult,
  buttonsConfig,
  disableEntityFields,
  anchorEl,
  anchorOrigin = {
    vertical: 52,
    horizontal: 'right',
  },
  transformOrigin = {
    vertical: 'top',
    horizontal: 'right',
  },
  customMaxHeight,
  useSpaceBetweenButtons = false,
  stableAnchor = false,
}: FilterPopoverProps<T>): JSX.Element => {
  const { classes } = useStyles();

  const closePopover = useCallback(
    (success: boolean) => {
      onClosePopover(success);
    },
    [onClosePopover]
  );

  const handleClickAway = useCallback(
    (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node;

      if (anchorEl?.contains(target)) {
        return;
      }

      closePopover(false);
    },
    [anchorEl, closePopover]
  );

  useEffect(() => {
    if (!stableAnchor || !openPopover) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        closePopover(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [closePopover, openPopover, stableAnchor]);

  const content = (
    <>
      {title && !hideTitle && (
        <Box className={classes.titleContainer}>
          <Typography variant="header2_medium">{title}</Typography>
        </Box>
      )}

      <Box
        sx={{
          maxHeight: customMaxHeight || POPOVER_MAX_HEIGHT,
          overflow: customMaxHeight === 'none' ? 'visible' : 'auto',
        }}
      >
        <PopoverComponent
          onCloseModal={closePopover}
          entity={entity}
          entityId={entityId}
          disableSubmit={disableSubmit}
          onResult={onResult}
          disableEntityFields={disableEntityFields}
        />
      </Box>

      {buttonsConfig && (
        <Box
          className={
            useSpaceBetweenButtons
              ? classes.actionButtonsContainerSpaceBetween
              : classes.actionButtonsContainer
          }
        >
          <ActionButtons buttons={buttonsConfig} />
        </Box>
      )}
    </>
  );

  if (stableAnchor) {
    return (
      <Popper
        open={openPopover}
        anchorEl={anchorEl}
        placement="bottom-end"
        modifiers={STABLE_ANCHOR_POPPER_MODIFIERS}
        className={classes.stableAnchorPopper}
        data-testid="filter-popover"
      >
        <ClickAwayListener onClickAway={handleClickAway}>
          <Paper elevation={8} className={classes.stableAnchorPaper}>
            <FocusTrap open={openPopover} disableRestoreFocus>
              <Box>{content}</Box>
            </FocusTrap>
          </Paper>
        </ClickAwayListener>
      </Popper>
    );
  }

  return (
    <Popover
      open={openPopover}
      anchorEl={anchorEl}
      onClose={() => closePopover(false)}
      anchorOrigin={anchorOrigin}
      transformOrigin={transformOrigin}
      data-testid="filter-popover"
      className={classes.popover}
    >
      {content}
    </Popover>
  );
};

export default FilterPopover;
